import React, { useMemo, useState } from 'react'
import { DatePicker, Table } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { useRequest } from 'ahooks'
import dayjs from 'dayjs'
import moment from 'moment'
import { useHistory } from 'react-router'
import { Expaned, Row } from '@/components/layout/layout'
import useBemClass from '@/utils/hooks/useBEMClass'
import $msg from '@/utils/message'
import { $post } from '@/api'
import { errorCallBack } from '@/utils'
import DataDateSelect, { ScopeType } from '../components/data-date-select/data-date-select'
import MemberDataChart from './member-data-chart'
import { MEMBER_RECHARGE_RECORD } from '../../member-manage/router'

import './member-data.scss'

interface MemberDataRow {
    date: string
    recharge_amount: number | string
    recharge_count: number
    consume_amount: number | string
    consume_count: number
    member_registered: number
}

// 会员数据仅支持日/周/月（后端查询范围最多 31 天，年汇总会超出限制）
const MEMBER_SCOPE_TYPES: ScopeType[] = ['date', 'week', 'month']

// 会员数据接口 scope 映射：索引 [日, 周, 月] -> 后端 [1, 2, 3]
const SCOPE_TO_API = [1, 2, 3]

// 会员数据各档位默认展示范围：日=近15天，周=近4周，月=当月（均在 31 天内）
const MEMBER_DAY_COUNT = 14
const MEMBER_WEEK_COUNT = 3
const MEMBER_MONTH_COUNT = 0

const DEFAULT_TIP = '当前展示近15日走势'

export const getMemberDataDateByScope = (scope: number) => {
    let from: string | undefined
    let to: string | undefined
    switch (MEMBER_SCOPE_TYPES[scope]) {
        case 'date':
            from = dayjs().subtract(MEMBER_DAY_COUNT, 'day').format('YYYY-MM-DD')
            to = dayjs().format('YYYY-MM-DD')
            break
        case 'week':
            from = dayjs().startOf('week').subtract(MEMBER_WEEK_COUNT, 'week').add(1, 'd').format('YYYY-MM-DD')
            to = dayjs().endOf('week').add(1, 'd').format('YYYY-MM-DD')
            break
        case 'month':
            from = dayjs().startOf('month').format('YYYY-MM-DD')
            to = dayjs().endOf('month').format('YYYY-MM-DD')
            break
        default:
            break
    }

    return [from, to]
}

export const formatMemberDataDate = (value: string, scope: number) => {
    if (!value) return '-'
    if (MEMBER_SCOPE_TYPES[scope] === 'week') {
        const week = Math.ceil(Number(value.substring(8, 10)) / 7)

        return `${Number(value.substring(5, 7))}月第${week}周`
    }
    if (MEMBER_SCOPE_TYPES[scope] === 'month') return `${dayjs(value).format('YY')}年${dayjs(value).format('M')}月`

    return value
}

const formatMoney = (value?: number | string) => {
    if (value == null || value === '') return '0.00'

    return Number(value).toFixed(2)
}

const buildSummaryRow = (list: MemberDataRow[]): MemberDataRow => {
    return {
        date: '累计',
        recharge_amount: list.reduce((sum, item) => sum + Number(item.recharge_amount || 0), 0),
        recharge_count: list.reduce((sum, item) => sum + Number(item.recharge_count || 0), 0),
        consume_amount: list.reduce((sum, item) => sum + Number(item.consume_amount || 0), 0),
        consume_count: list.reduce((sum, item) => sum + Number(item.consume_count || 0), 0),
        member_registered: list.reduce((sum, item) => sum + Number(item.member_registered || 0), 0),
    }
}

const MemberData: React.FC = () => {
    const cls = useBemClass('member-data')
    const history = useHistory()
    const [scope, setScope] = useState<number>(0)
    const [date, setDate] = useState<(string | undefined)[]>(getMemberDataDateByScope(0))
    const [tip, setTip] = useState<string>(DEFAULT_TIP)
    const [showTip, setShowTip] = useState<boolean>(true)

    const req = useRequest<MemberDataRow[]>(
        () =>
            $post({
                url: '/finance/getmembershipstatistics',
                params: {
                    from_date: date[0],
                    to_date: date[1],
                    scope: SCOPE_TO_API[scope],
                },
            }),
        {
            refreshDeps: [date, scope],
            ready: !!(date[0] && date[1]),
            onError: errorCallBack,
        },
    )

    const summaryRow = useMemo(() => (req.data && req.data.length > 0 ? buildSummaryRow(req.data) : null), [req.data])

    const dataSource = summaryRow ? [summaryRow, ...(req.data || [])] : []

    const summaryData = useMemo(() => {
        if (!summaryRow) return []

        return [
            { id: 'recharge', name: '充值金额', amount: summaryRow.recharge_amount, isMoney: true },
            { id: 'consume', name: '余额消费', amount: summaryRow.consume_amount, isMoney: true },
            { id: 'registered', name: '新增会员', amount: summaryRow.member_registered, isMoney: false },
        ]
    }, [summaryRow])

    // 详情钻取：累计行用整个筛选区间，普通行按当前汇总维度还原出该周期的起止日期
    const openRechargeRecord = (row: MemberDataRow, index: number) => {
        let startDate = row.date
        let endDate = row.date

        if (index === 0) {
            startDate = date[0] as string
            endDate = date[1] as string
        } else if (MEMBER_SCOPE_TYPES[scope] === 'week') {
            startDate = dayjs(row.date).startOf('week').add(1, 'd').format('YYYY-MM-DD')
            endDate = dayjs(row.date).endOf('week').add(1, 'd').format('YYYY-MM-DD')
        } else if (MEMBER_SCOPE_TYPES[scope] === 'month') {
            startDate = dayjs(row.date).startOf('month').format('YYYY-MM-DD')
            endDate = dayjs(row.date).endOf('month').format('YYYY-MM-DD')
        }

        history.push(`${MEMBER_RECHARGE_RECORD}?start_date=${startDate}&end_date=${endDate}`)
    }

    const columns: ColumnsType<MemberDataRow> = [
        {
            title: '时间',
            dataIndex: 'date',
            width: 140,
            render: (value, _row, index) => (index === 0 ? value : formatMemberDataDate(value, scope)),
        },
        {
            title: '充值金额(元)',
            dataIndex: 'recharge_amount',
            width: 140,
            align: 'right',
            render: value => formatMoney(value),
        },
        {
            title: '充值笔数',
            dataIndex: 'recharge_count',
            width: 120,
            align: 'right',
            render: value => Number(value),
        },
        {
            title: '余额消费金额(元)',
            dataIndex: 'consume_amount',
            width: 160,
            align: 'right',
            render: value => formatMoney(value),
        },
        {
            title: '余额消费笔数',
            dataIndex: 'consume_count',
            width: 120,
            align: 'right',
            render: value => Number(value),
        },
        {
            title: '新增会员',
            dataIndex: 'member_registered',
            width: 120,
            align: 'right',
            render: value => Number(value),
        },
        {
            title: '操作',
            width: 80,
            align: 'center',
            render: (_value, row, index) => (
                <div className="action" onClick={() => openRechargeRecord(row, index)}>
                    详情
                </div>
            ),
        },
    ]

    return (
        <Expaned data-column data-overflow-hidden className={cls('content')}>
            <Row data-align-items-center data-margin-top-16 data-justify-content-space-between>
                <Row data-align-items-center>
                    <DataDateSelect
                        types={MEMBER_SCOPE_TYPES}
                        day={MEMBER_DAY_COUNT}
                        week={MEMBER_WEEK_COUNT}
                        month={MEMBER_MONTH_COUNT}
                        onClick={(from, to, nextScope, t) => {
                            setDate([from, to])
                            setScope(nextScope)
                            setTip(t)
                            setShowTip(true)
                        }}
                    />
                </Row>
                <Row data-align-items-center>
                    <div hidden={!showTip} data-color-999 data-font-size-14 data-margin-right-10>
                        {tip}
                    </div>
                    <div className="time-picker">
                        <DatePicker.RangePicker
                            allowClear={false}
                            picker={MEMBER_SCOPE_TYPES[scope] as any}
                            dateRender={current => (
                                <div className="ant-picker-cell-inner" style={{ borderRadius: '50%' }}>
                                    {current.date()}
                                </div>
                            )}
                            value={[moment(date[0]), moment(date[1])]}
                            onChange={monent => {
                                if (!monent) return

                                setShowTip(false)
                                if (MEMBER_SCOPE_TYPES[scope] === 'month') {
                                    const start_date = monent[0]?.format('YYYY-MM-DD')
                                    const end_date = monent[1]
                                        ?.set('date', 1)
                                        .add(1, 'month')
                                        .subtract(1, 'days')
                                        .format('YYYY-MM-DD')
                                    setDate([start_date, end_date])
                                } else {
                                    if (!monent[0]) return

                                    const start_date = monent[0].format('YYYY-MM-DD')
                                    const end_date = (monent[1] || monent[0]).format('YYYY-MM-DD')
                                    const days = moment(end_date).diff(moment(start_date), 'day') + 1
                                    if (MEMBER_SCOPE_TYPES[scope] === 'date' && days > 31) {
                                        $msg('warning', '会员数据最多查询 31 天')

                                        return
                                    }
                                    setDate([start_date, end_date])
                                }
                            }}
                            suffixIcon={<i className="iconfont iconshijianshaixuan" />}
                        />
                    </div>
                </Row>
            </Row>
            <Row className={cls('sum')}>
                {summaryData.map(s => (
                    <div key={s.id} className={cls('total')}>
                        <div className={cls('total', 'num')}>
                            {s.isMoney ? '¥' : ''}
                            {s.isMoney ? Number(s.amount).toFixed(2) : s.amount}
                        </div>
                        <div className={cls('total', 'des')}>{s.name}</div>
                    </div>
                ))}
            </Row>
            <Expaned data-overflow-auto className={cls('no-scroll')}>
                <div className={cls('chart')}>
                    <MemberDataChart dataList={req.data || []} scope={scope} />
                </div>
                <div className={cls('table-wrap')}>
                    <Table
                        rowKey={(record, index) => `${record.date}-${index}`}
                        bordered
                        loading={req.loading}
                        columns={columns}
                        dataSource={dataSource}
                        pagination={false}
                        scroll={{ x: 'max-content' }}
                    />
                </div>
            </Expaned>
        </Expaned>
    )
}

export default MemberData
